import {
  DefaultAlertDialog,
  DefaultModalDialog,
  DefaultNumberInputField,
  DefaultScreenTileComponentBox,
  DistanceBetween,
  DurationViewer,
  LabeledText,
  PictureViewer,
  useToast,
  InputField,
} from '~/common/components/defaultUIComponents';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Tooltip,
  Button,
} from '@chakra-ui/react';
import SpecialJobApi from '~/common/api/zeiterfassungServerApi/SpecialJobApi';
import { SpecialJob } from '~/common/entities/SpecialJobs/SpecialJobsTypes';
import { CheckCircleIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { PictureType } from '~/common/entities/Pictures/PictureTypes';
import { momentDurationToSeconds } from '~/common/utils/timeUtils';
import useTranslate from '~/common/utils/translate';
import React, { useState } from 'react';
import * as Yup from 'yup';

interface SpecialJobInnerTileInput {
  specialJob: SpecialJob;
  employee: Employee;
}

const SpecialJobInnerTile: React.FC<SpecialJobInnerTileInput> = ({
  specialJob,
  employee,
}) => {
  const [openDelete, setOpenDelete] = useState(false);
  const [openPictures, setOpenPictures] = useState(false);
  const translate = useTranslate();
  const toast = useToast({ position: 'bottom' });

  const [openEndModal, setOpenEndModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const { mutateAsync: endSpecialJobMutation } =
    SpecialJobApi.hooks.useEndSpecialJobMutation();

  const { mutateAsync: editSpecialJobInstanceMutation } =
    SpecialJobApi.hooks.useEditSpecialJobInstanceMutation();

  const { mutateAsync: deleteSpecialJobMutation } =
    SpecialJobApi.hooks.useDeleteJobMutation();

  const toggleOpenDelete = () => {
    setOpenDelete(!openDelete);
  };

  const toggleEndModal = () => {
    setOpenEndModal(!openEndModal);
  };

  const toggleOpenPictures = () => {
    setOpenPictures(!openPictures);
  };

  const deleteSpecialJob = () => {
    const showErrorToastWhenSpecialJobIsRunning = () => {
      toast({
        title: 'specialJobInnerTile.specialJobsIsRunning.toast.title',
        description:
          'specialJobInnerTile.specialJobsIsRunning.toast.description',
        status: 'error',
      });
    };

    deleteSpecialJobMutation(specialJob).catch(
      showErrorToastWhenSpecialJobIsRunning,
    );
  };

  const handleSubmitEditSpecialJobInstance = (values: {
    duration: number;
    description: string;
  }) => {
    editSpecialJobInstanceMutation({
      specialJob_id: specialJob.id,
      employee_id: employee.id,
      totalSeconds: values.duration * 60,
      description: values.description,
    })
      .then(() =>
        toast({
          status: 'success',
          title: 'specialJobInnerTile.edit.success.title',
          description: 'specialJobInnerTile.edit.success.body',
          duration: 1500,
        }),
      )
      .catch(() =>
        toast({
          status: 'error',
          title: 'specialJobInnerTile.edit.error.title',
          description: 'specialJobInnerTile.edit.error.body',
          duration: 1500,
        }),
      );

    setOpenEditModal(false);
  };

  const handleEndSpecialJob = () => {
    toggleEndModal();
  };

  const handleSubmitEndJob = (values: { duration: number; amount: number }) => {
    endSpecialJobMutation({
      specialJob_id: specialJob.id,
      employee_id: employee.id,
      totalSeconds: values.duration * 60,
      doneAmount: values.amount,
    })
      .then(() =>
        toast({
          status: 'success',
          title: 'specialJobInnerTile.end.success.title',
          description: 'specialJobInnerTile.end.success.body',
          duration: 1500,
        }),
      )
      .catch(() =>
        toast({
          status: 'error',
          title: 'specialJobInnerTile.end.error.title',
          description: 'specialJobInnerTile.end.error.body',
          duration: 1500,
        }),
      );

    toggleEndModal();
  };

  if (!specialJob) {
    return null;
  }

  return (
    <DefaultScreenTileComponentBox
      distanceBetweenTileComponents={DistanceBetween.narrow}
    >
      <DefaultModalDialog
        isOpen={openEndModal}
        onCancel={toggleEndModal}
        dialogHeaderText="specialJobInnerTile.end.modal.title"
        submitText="specialJobInnerTile.end.modal.submit"
        showCancelButton={true}
        onSubmit={(values) => handleSubmitEndJob(values)}
        initialValues={{
          duration: Math.floor(
            (specialJob.employee_durations.find(
              (ed) => ed.employee_id === employee.id,
            )?.duration_seconds ?? 0) / 60,
          ),
          amount: 0,
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validateOnMount={false}
        validationSchema={Yup.object().shape({
          duration: Yup.number().moreThan(
            0,
            'specialJobInnerTile.end.modal.duration.warning',
          ),
          amount: Yup.number().moreThan(
            0,
            'specialJobInnerTile.end.modal.amount.warning',
          ),
        })}
      >
        <Flex direction="row" gap={4}>
          <DefaultNumberInputField
            name="duration"
            label="specialJobInnerTile.end.modal.duration.title"
            min={0}
          />
          <DefaultNumberInputField
            name="amount"
            label="specialJobInnerTile.end.modal.amount.title"
            min={0}
          />
        </Flex>
      </DefaultModalDialog>
      <DefaultModalDialog
        isOpen={openEditModal}
        onCancel={() => setOpenEditModal(false)}
        dialogHeaderText="specialJobInnerTile.edit.modal.title"
        submitText="specialJobInnerTile.edit.modal.submit"
        showCancelButton={true}
        onSubmit={(values) => handleSubmitEditSpecialJobInstance(values)}
        initialValues={{
          duration: Math.floor(
            (momentDurationToSeconds(specialJob.special_job_duration) ?? 0) /
              60,
          ),
          description: specialJob.description ?? '',
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validateOnMount={false}
        validationSchema={Yup.object().shape({
          duration: Yup.number().moreThan(
            0,
            'specialJobInnerTile.edit.modal.duration.warning',
          ),
          description: Yup.string().min(
            4,
            'specialJobInnerTile.edit.modal.description.warning',
          ),
        })}
      >
        <Flex direction="row" gap={4}>
          <DefaultNumberInputField
            name="duration"
            label="specialJobInnerTile.end.modal.duration.title"
            min={0}
          />
          <InputField
            name="description"
            label="createSpecialJob.description.label"
            placeholder=""
            mb={5}
            textarea
          />
        </Flex>
      </DefaultModalDialog>
      <Grid templateColumns="repeat(5, 1fr)">
        <GridItem
          colSpan={2}
          display="flex"
          alignContent="center"
          alignItems="center"
        >
          {specialJob.done && (
            <Box pr={2} pb={2}>
              <CheckCircleIcon color="green.500" />
            </Box>
          )}
          <LabeledText
            label={specialJob.name || 'specialJobTile.noName'}
            fontSize="md"
            labelProps={{ as: 'u' }}
          />
        </GridItem>

        <GridItem rowSpan={2}>
          <PictureViewer
            pictureIds={specialJob.special_job_pictures}
            projectOrJobId={specialJob.id}
            pictureType={PictureType.SPECIALJOB}
            showInModal={false}
            mt={2}
            mr={0}
            cursor="pointer"
            onClick={toggleOpenPictures}
            showDropArea={false}
          />
          <DefaultModalDialog
            dialogHeaderText="specialJobTile.showPictures.dialogHeaderText"
            submitText="specialJobTile.showPictures.submitText"
            isOpen={openPictures}
            onCancel={toggleOpenPictures}
            onSubmit={toggleOpenPictures}
            showCancelButton={false}
            size="4xl"
            initialValues={{}}
          >
            <PictureViewer
              pictureIds={specialJob.special_job_pictures}
              projectOrJobId={specialJob.id}
              pictureType={PictureType.SPECIALJOB}
              closeModal={toggleOpenPictures}
            />
          </DefaultModalDialog>
        </GridItem>
        <GridItem colSpan={2}>
          <Flex justify="flex-end">
            <Tooltip
              label={translate('specialJobInnerTile.end.button')}
              aria-label={translate('specialJobInnerTile.end.button')}
            >
              <Button
                onClick={handleEndSpecialJob}
                mr={2}
                isDisabled={
                  !specialJob.employee_durations.some(
                    (ed) => ed.employee_id === employee.id,
                  )
                }
              >
                {translate('specialJobInnerTile.end.button')}
              </Button>
            </Tooltip>
            <Tooltip
              label={translate('specialJobTile.editSpecialJob.tooltip.label')}
              aria-label={translate(
                'specialJobTile.editSpecialJob.tooltip.label',
              )}
            >
              <IconButton
                icon={<EditIcon />}
                onClick={() => setOpenEditModal(true)}
                aria-label={
                  translate(
                    'specialJobTile.editSpecialJob.tooltip.label',
                  ) as string
                }
                isRound
                mr={1}
              />
            </Tooltip>

            <Tooltip
              label={translate('specialJobTile.deleteSpecialJob.tooltip.label')}
              aria-label={translate(
                'specialJobTile.deleteSpecialJob.tooltip.label',
              )}
            >
              <IconButton
                icon={<DeleteIcon />}
                onClick={toggleOpenDelete}
                aria-label={
                  translate(
                    'specialJobTile.deleteSpecialJob.tooltip.label',
                  ) as string
                }
                isRound
              />
            </Tooltip>

            <DefaultAlertDialog
              dialogHeaderText="specialJobTile.deleteSpecialJob.dialogHeaderText"
              dialogBodyText="specialJobTile.deleteSpecialJob.dialogBodyText"
              isOpen={openDelete}
              onConfirmedAction={deleteSpecialJob}
              onCancel={toggleOpenDelete}
            />
          </Flex>
        </GridItem>
        <GridItem>
          <LabeledText label="specialJobInnerTile.ordered">
            {specialJob.ordered_amount}
          </LabeledText>
        </GridItem>
        <GridItem>
          <LabeledText label="specialJobInnerTile.remaining">
            {Math.max(
              specialJob.ordered_amount - specialJob.total_done_amount,
              0,
            )}
          </LabeledText>
        </GridItem>
        <GridItem colSpan={2} />
        <GridItem>
          <DurationViewer
            durationInSeconds={momentDurationToSeconds(specialJob.duration)}
            showSeconds
            label="specialJobInnerTile.duration"
          />
        </GridItem>

        <GridItem colSpan={2}>
          {specialJob.special_job_duration != null && (
            <DurationViewer
              durationInSeconds={specialJob.special_job_duration}
              label="specialJobInnerTile.actualDurationForDoneAmount"
            />
          )}
        </GridItem>
        <GridItem colSpan={2} />

        <GridItem>
          <LabeledText label="specialJobInnerTile.Date">
            {new Date(specialJob.created_at).toLocaleDateString('de-DE')}
          </LabeledText>
        </GridItem>
        <GridItem>
          {specialJob.employee_durations.some(
            (ed) => ed.employee_id === employee.id,
          ) && (
            <DurationViewer
              durationInSeconds={Math.floor(
                specialJob.employee_durations.find(
                  (ed) => ed.employee_id === employee.id,
                )?.duration_seconds ?? 0,
              )}
              label="specialJobInnerTile.currentDuration"
            />
          )}
        </GridItem>
        <GridItem colSpan={4}>
          <LabeledText label="specialJobInnerTile.description">
            {specialJob.description}
          </LabeledText>
        </GridItem>
        <GridItem>
          {specialJob.message && (
            <LabeledText label="specialJobInnerTile.message">
              {specialJob.message}
            </LabeledText>
          )}
        </GridItem>
      </Grid>
    </DefaultScreenTileComponentBox>
  );
};

export default SpecialJobInnerTile;
